import { Suspense, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loading from "../../../../common/components/ui/loading/Loading";
import {
  IMaintenance,
  MAINTENANCE_AUDIT_STATUS,
  MAINTENANCE_STATUS,
  MaintenanceType,
} from "../../../../types/maintenance.type";
import {
  TableFilters,
  TableFiltersState,
} from "../../../../common/components/ui/table/TableFilters";
import GenericTable from "../../../../common/components/ui/table/GenericTable";
import TableFooter from "../../../../common/components/ui/table/TableFooter";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { getMaintenanceAudit } from "../../../../common/services/maintenance.api";

import { formatDateAndTime } from "../../../../common/helpers/date.helpers";

import { IVehicle } from "../../../../types/vehicle.type";

import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { useCompanyStore } from "@/common/states/store/Company";
import PendenciesMaintenance from "@/modules/maintenances/components/PendenciesMaintenance";
import ToCheckModal from "../modals/ToCheckModal";
import { HiCheckCircle, HiExclamationTriangle } from "react-icons/hi2";

const STATUS_LABELS = {
  [MAINTENANCE_STATUS.Finalize]: "Finalizado",
};
const AUDIT_STATUS_LABELS = {
  [MAINTENANCE_AUDIT_STATUS.NotAudited]: "Não auditada",
  [MAINTENANCE_AUDIT_STATUS.Audited]: "Auditada",
  [MAINTENANCE_AUDIT_STATUS.Contested]: "Contestada",
  [MAINTENANCE_AUDIT_STATUS.ReplyContested]: "Contestação respondida",
};

function getStatusLabels(status: MAINTENANCE_STATUS) {
  return STATUS_LABELS[status] || status;
}
const EMPTY_ACTION = { eventName: "", data: undefined };

const headers = {
  licensePlate: "Placa",
  name: "Cliente",
  waitingTicket: "Senha",
  status: "Status",
  mechanic: "Mecânico",
  typePlanRenter: "Plano do cliente",
  verificationStatus: "Verificação",
  updatedAt: "Atualizado em",
  createdAt: "Criado em",
  typeMaintenance: "Tipo de Manutenção",
};

function TableMaintenanceCompleted() {
  useContext(AuthContext);
  const navigate = useNavigate();
  const { notifyError } = useToastContext();
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [maintenance, setMaintenance] = useState<IMaintenance[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>(
    EMPTY_ACTION
  );
  const { selectedCompanies, filterMode } = useCompanyStore();

  const actions = [
    {
      label: "Verificar",
      action: (data) => setAction({ eventName: "toCheck", data: data }),
    },
  ];
  const isAction = (eventName: string) => action.eventName === eventName;
  const cleanActionData = () => {
    setAction(EMPTY_ACTION);
  };

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [MAINTENANCE_STATUS.Finalize],
    filterColumn: "",
    orderBy: "-updatedAt",
    startDate: null,
    endDate: null,
    auditStatus: Object.keys(AUDIT_STATUS_LABELS).join(","),
  });

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    if (newFilters.status !== undefined) {
      const { status: newAuditStatusArray, ...otherFilters } = newFilters;

      setFilters((prevFilters) => ({
        ...prevFilters,
        ...otherFilters,
        auditStatus:
          newAuditStatusArray.length > 0 ? newAuditStatusArray.join(",") : "",
      }));
      return;
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  };

  useEffect(() => {
    async function loadMaintenance(filters, page) {
      setIsLoading(true);
      const result = await getMaintenanceAudit({
        ...filters,
        page,
        status: MAINTENANCE_STATUS.Finalize,
        auditStatus: filters.auditStatus,
      });
      setData({
        itemsPerPage: data.itemsPerPage,
        totalItems: result.totalItems,
      });
      if (!result.ok) {
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        notifyError(
          (result as { message?: string })?.message || "unknown error"
        );
        return;
      }
      const sortedData = (result.data as IMaintenance[]).sort((a, b) => {
        if (a.waitingTicket && b.waitingTicket) {
          return a.waitingTicket - b.waitingTicket;
        }
        return 0;
      });
      setMaintenance(sortedData);
      setIsLoading(false);
    }
    loadMaintenance(filters, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, selectedCompanies, filterMode, reload]);

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Auditoria de Manutenções"
          placeholder="Digite o nome do cliente, a placa, o mecânico, o chassi ou a senha."
          statusOptions={AUDIT_STATUS_LABELS}
          onSearch={handleFilterChange}
          entity="maintenance"
          tableData={maintenance}
        />

        <GenericTable
          headers={headers}
          data={maintenance}
          actions={actions}
          setFilters={({ orderBy }) => {
            setFilters((prevFilters) => ({
              ...prevFilters,
              orderBy,
            }));
          }}
          renders={
            {
              licensePlate: (licensePlate, vehicle) => (
                <a
                  className="text-blue-600 hover:text-bluePrimary cursor-pointer"
                  onClick={() =>
                    navigate(`/veiculos/${(vehicle as IVehicle).licensePlate}`)
                  }
                  target="_blank"
                >
                  {licensePlate}
                </a>
              ),
              waitingTicket: (waitingTicket) => waitingTicket || "-",
              status: getStatusLabels,
              mechanic: (mechanic) => mechanic || "-",
              pendencies: (_, maintenance) => (
                <PendenciesMaintenance
                  maintenance={maintenance as MaintenanceType}
                />
              ),
              verificationStatus: (_, maintenance) => {
                const chargeInfo = (maintenance as IMaintenance)
                  .chargeMaintenance;

                if (chargeInfo?.contested) {
                  return (
                    <div className="flex justify-center" title="Contestado">
                      <HiExclamationTriangle className="h-6 w-6 text-yellow-500" />
                    </div>
                  );
                }
                if (chargeInfo?.confirmed) {
                  return (
                    <div className="flex justify-center" title="Confirmado">
                      <HiCheckCircle className="h-6 w-6 text-green-500" />
                    </div>
                  );
                }
                return null;
              },
              updatedAt: formatDateAndTime,
              createdAt: formatDateAndTime,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          }
          entity="maintenance"
        />
        {isLoading && <Loading />}

        <TableFooter
          page={page || 1}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
      {isAction("toCheck") && (
        <ToCheckModal
          onClosed={cleanActionData}
          licensePlate={(action.data as IMaintenance)?.licensePlate}
          maintenanceId={(action.data as IMaintenance)?._id}
          chargeStatus={(action.data as IMaintenance)?.chargeMaintenance}
          companyId={(action.data as IMaintenance)?.companyId}
          setReload={setReload}
          reload={reload}
        />
      )}
    </Suspense>
  );
}

export default TableMaintenanceCompleted;
